import React, { useState, useRef } from 'react'
import { ArrowUp, ArrowDown, ArrowUpDown, Trash2 } from 'lucide-react'
import { AdImpactClient } from '../../lib/alertClientsApi'
import ScrollableTableShell from '../ScrollableTableShell'
import TableScrollFab from '../TableScrollFab'
import InlineEditCell from './InlineEditCell'
import BooleanToggle from './BooleanToggle'

const YELLOW = '#eab308'
const MUTED = '#6b7280'
const ICON_IDLE = '#d1d5db'

const BOOL_COLS = [
  { key: 'delta_alerts',       label: 'Delta'   },
  { key: 'admo_alerts',        label: 'AdMo'    },
  { key: 'admo_plus_alerts',   label: 'AdMo+'   },
  { key: 'ctv_alerts',         label: 'CTV'     },
  { key: 'dashboards',         label: 'Dash'    },
  { key: 'digital_dashboards', label: 'Digital' },
]

const COLUMNS = [
  { key: 'client_name',       label: 'Client Name', minWidth: 160 },
  { key: 'email',             label: 'Email',        minWidth: 200 },
  { key: 'office',            label: 'Office',       minWidth: 130 },
  { key: 'state',             label: 'State',        minWidth: 70 },
  { key: 'election',          label: 'Election',     minWidth: 110 },
  { key: 'date_added',        label: 'Date Added',   minWidth: 100 },
  { key: 'account_executive', label: 'Acct Exec',    minWidth: 120 },
  { key: 'year',              label: 'Year',         minWidth: 70 },
  ...BOOL_COLS.map(c => ({ key: c.key, label: c.label, minWidth: 68, isBool: true })),
]

function formatDate(val) {
  if (!val) return ''
  const d = new Date(val + 'T00:00:00')
  if (isNaN(d)) return val
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
}

function SortIcon({ active, asc }) {
  const color = !active ? ICON_IDLE : YELLOW
  const wrap = { display: 'inline-flex', color, verticalAlign: 'middle' }
  if (!active) {
    return (
      <span style={wrap} aria-hidden>
        <ArrowUpDown size={12} />
      </span>
    )
  }
  return (
    <span style={wrap} aria-hidden>
      {asc ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
    </span>
  )
}

function AlertClientTable({ clients, onRefresh }) {
  const scrollRef = useRef(null)
  const [sortKey, setSortKey] = useState('client_name')
  const [sortDir, setSortDir] = useState('asc')
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [hoveredRow, setHoveredRow] = useState(null)

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    else { setSortKey(key); setSortDir('asc') }
  }

  const sorted = [...clients].sort((a, b) => {
    let av = a[sortKey]
    let bv = b[sortKey]
    if (typeof av === 'boolean') { av = av ? 1 : 0; bv = bv ? 1 : 0 }
    if (av == null) av = ''
    if (bv == null) bv = ''
    if (typeof av === 'string') {
      const c = av.localeCompare(bv, undefined, { sensitivity: 'base' })
      return sortDir === 'asc' ? c : -c
    }
    return sortDir === 'asc' ? av - bv : bv - av
  })

  const handleUpdate = async (id, field, value) => {
    try {
      await AdImpactClient.update(id, { [field]: value })
      onRefresh()
    } catch (err) {
      console.error('Update failed:', err)
    }
  }

  const confirmAndDelete = async (id) => {
    try {
      await AdImpactClient.delete(id)
      setConfirmDelete(null)
      onRefresh()
    } catch (err) {
      console.error('Delete failed:', err)
    }
  }

  if (clients.length === 0) {
    return (
      <div style={{
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        backgroundColor: '#fff',
        padding: '64px 32px',
        textAlign: 'center',
        fontSize: 14,
        color: '#6b7280',
      }}
      >
        No clients match the current filters.
      </div>
    )
  }

  const thBase = {
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    cursor: 'pointer',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    borderBottom: '1px solid #e5e7eb',
  }

  return (
    <div style={{ position: 'relative', width: '100%', minWidth: 0, maxWidth: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        backgroundColor: '#fff',
        boxShadow: 'var(--shadow-sm)',
        boxSizing: 'border-box',
      }}
      >
        <ScrollableTableShell ref={scrollRef} minTableWidth="1400px" forceHorizontalScroll fillParent>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                {COLUMNS.map(col => {
                  const active = sortKey === col.key
                  return (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      style={{
                        ...thBase,
                        minWidth: col.minWidth,
                        color: active ? YELLOW : MUTED,
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                        {col.label}
                        <SortIcon active={active} asc={sortDir === 'asc'} />
                      </div>
                    </th>
                  )
                })}
                <th style={{ ...thBase, width: 44, cursor: 'default', minWidth: 44 }} />
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, idx) => (
                <tr
                  key={row.id}
                  onMouseEnter={() => setHoveredRow(row.id)}
                  onMouseLeave={() => setHoveredRow(null)}
                  style={{
                    borderBottom: '1px solid #f3f4f6',
                    backgroundColor: idx % 2 === 0 ? '#fff' : 'rgba(249, 250, 251, 0.6)',
                    transition: 'background-color 0.15s',
                    ...(hoveredRow === row.id ? { backgroundColor: '#fffbeb' } : {}),
                  }}
                >
                  {COLUMNS.map(col => (
                    <td key={col.key} style={{ padding: '8px 12px', verticalAlign: 'middle' }}>
                      {col.isBool ? (
                        <BooleanToggle
                          value={!!row[col.key]}
                          onChange={val => handleUpdate(row.id, col.key, val)}
                        />
                      ) : col.key === 'date_added' ? (
                        <InlineEditCell
                          value={formatDate(row.date_added)}
                          field="date_added"
                          onSave={val => handleUpdate(row.id, 'date_added', val)}
                        />
                      ) : (
                        <InlineEditCell
                          value={row[col.key]}
                          field={col.key}
                          onSave={val => handleUpdate(row.id, col.key, val)}
                        />
                      )}
                    </td>
                  ))}
                  <td style={{ padding: '8px', textAlign: 'center', verticalAlign: 'middle' }}>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(row.id)}
                      title="Delete this client"
                      style={{
                        display: 'inline-flex',
                        borderRadius: 4,
                        padding: 4,
                        border: 'none',
                        background: 'transparent',
                        color: '#9ca3af',
                        cursor: 'pointer',
                        opacity: hoveredRow === row.id ? 1 : 0.35,
                        transition: 'opacity 0.15s, color 0.15s, background 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = '#ef4444'
                        e.currentTarget.style.backgroundColor = '#fef2f2'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = '#9ca3af'
                        e.currentTarget.style.backgroundColor = 'transparent'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollableTableShell>
      </div>

      <TableScrollFab scrollRef={scrollRef} />

      {confirmDelete && (
        <div
          role="presentation"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(15, 23, 42, 0.5)',
          }}
          onClick={() => setConfirmDelete(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-alert-title"
            style={{
              margin: '0 16px',
              width: '100%',
              maxWidth: 420,
              borderRadius: 12,
              backgroundColor: '#fff',
              padding: 28,
              boxShadow: 'var(--shadow-xl)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 id="delete-alert-title" style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600, color: '#111827' }}>
              Delete alert client?
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: 14, lineHeight: 1.6, color: '#6b7280' }}>
              This action cannot be undone. The client and their alert preferences will be permanently removed.
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                style={{
                  borderRadius: 6,
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#fff',
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#4b5563',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => confirmAndDelete(confirmDelete)}
                style={{
                  borderRadius: 6,
                  border: 'none',
                  backgroundColor: '#ef4444',
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#fff',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AlertClientTable
